// Classic chart patterns drawn on a chart: the swing points that make the shape (a thin line through
// them), the breakout level (dashed, from where it applies to the breakout or the latest candle), and a
// marker on the breakout candle. Used by both the days chart and the intraday chart; every time given
// must be a candle time already on the chart's axis.
import { LineSeries, LineStyle, type IChartApi, type ISeriesApi, type SeriesMarker, type Time, type UTCTimestamp } from 'lightweight-charts';
import { BEARISH_PATTERNS, type PatternName } from '@planb/core';
import { palette } from './palette';

export interface PatternDraw {
  name: PatternName;
  /** Swing points (time, price), oldest first. */
  pts: { t: number; v: number }[];
  /** The breakout level's two ends. */
  level: { t: number; v: number }[];
  /** The breakout candle's time, or null while the pattern is still forming. */
  breakT: number | null;
}

const short: Record<PatternName, string> = {
  'double bottom': 'Double bottom', 'double top': 'Double top', 'inverse head & shoulders': 'Inv. H&S', 'head & shoulders': 'H&S',
  'falling wedge': 'Falling wedge', 'rising wedge': 'Rising wedge', 'bull flag': 'Bull flag', 'bear flag': 'Bear flag',
  'bullish pennant': 'Pennant', 'bearish pennant': 'Pennant', 'ascending triangle': 'Asc. triangle', 'descending triangle': 'Desc. triangle',
};
export const patternLabel = (n: PatternName) => short[n];

/** Strictly increasing times (patterns mapped onto weekly candles can share a candle: keep the last). */
const uniq = (pts: { t: number; v: number }[]) => {
  const out: { time: UTCTimestamp; value: number }[] = [];
  for (const p of [...pts].sort((a, b) => a.t - b.t)) {
    if (out.length && out[out.length - 1].time === p.t) out[out.length - 1].value = p.v;
    else out.push({ time: p.t as UTCTimestamp, value: p.v });
  }
  return out;
};

export class PatternLayer {
  private series: ISeriesApi<'Line'>[] = [];
  private list: PatternDraw[] = [];
  private sig = '';
  constructor(private chart: IChartApi) {}

  /** Draw these patterns (an unchanged list is left alone). Returns their breakout markers, which the
   * caller merges with its own (a series has a single marker set). */
  set(list: PatternDraw[]): SeriesMarker<Time>[] {
    const sig = JSON.stringify(list) + palette().navy; // a theme change redraws in its colours
    if (sig !== this.sig) {
      this.sig = sig; this.list = list;
      for (const s of this.series) this.chart.removeSeries(s);
      this.series = [];
      const C = palette();
      for (const p of list) {
        const bull = !BEARISH_PATTERNS.has(p.name), forming = p.breakT === null;
        const shape = uniq(p.pts), level = uniq(p.level);
        if (shape.length >= 2) {
          const s = this.chart.addSeries(LineSeries, { color: C.navy, lineWidth: 1, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false });
          s.setData(shape); this.series.push(s);
        }
        if (level.length >= 2) {
          const s = this.chart.addSeries(LineSeries, {
            color: bull ? C.gd : C.rd, lineWidth: 1, lineStyle: LineStyle.Dashed, priceLineVisible: false, crosshairMarkerVisible: false,
            lastValueVisible: forming, title: forming ? `${bull ? '▲' : '▼'} ${short[p.name]} · forming` : '',
          });
          s.setData(level); this.series.push(s);
        }
      }
    }
    return this.markers();
  }

  markers(): SeriesMarker<Time>[] {
    const C = palette();
    return this.list.filter((p) => p.breakT !== null).map((p) => {
      const bull = !BEARISH_PATTERNS.has(p.name);
      return { time: p.breakT as UTCTimestamp, position: bull ? 'belowBar' as const : 'aboveBar' as const, shape: 'circle' as const, size: 0.6, color: C.navy, text: short[p.name] };
    });
  }
}
